import React from 'react';
import { FormattedMessage } from 'react-intl';
import _get from 'lodash/get';
import { hooks } from '@lifesize/clients.sdk';
import { colors } from '@lifesize/ux-assets';
import { ReactComponent as HandIcon } from '@lifesize/ux-assets/vectors/svgIcons/hand.svg';
import { ReactComponent as MicIcon } from '@lifesize/ux-assets/vectors/svgIcons/mic.svg';
import { makeStyles, Theme } from '@material-ui/core';
import { participantFilterType } from 'constants/ParticipantList';

interface props {
  selectParticipantFilter: Function;
  participantFilter: string;
}

export const FilterParticipantsSection = ({ participantFilter, selectParticipantFilter }: props) => {
  const raisedHands = hooks.useParticipantsWithRaisedHands();
  const raisedHandsCount = _get(raisedHands, 'length', 0);
  const participants = hooks.useParticipants();
  const unmuted = participants.filter((p) => !(p.localAudioMuteState || p.serverAudioMuteState));
  const unmutedCount = _get(unmuted, 'length', 0);
  const classes = useStyles({});

  const handleFilterNone = () => {
    selectParticipantFilter(participantFilterType.none);
  };

  const handleFilterHandsRaised = () => {
    selectParticipantFilter(participantFilterType.raisedHands);
  };

  const handleFilterUnmuted = () => {
    selectParticipantFilter(participantFilterType.unmuted);
  };

  return (
    <div className={classes.container}>
      <button
        data-testid="allParticipants"
        disabled={participantFilter === participantFilterType.none}
        onClick={handleFilterNone}
      >
        <span>
          <FormattedMessage
            id="components.inCall.participantList.filter.all"
            defaultMessage="All   {total}"
            values={{ total: _get(participants, 'length', 0) }}
          />
        </span>
      </button>
      <button
        data-testid="handRaisedParticipants"
        disabled={participantFilter === participantFilterType.raisedHands}
        onClick={handleFilterHandsRaised}
      >
        <HandIcon className={!!raisedHandsCount ? 'raisedHands' : ''} />
        <span>{raisedHandsCount}</span>
      </button>
      <button
        data-testid="unmutedParticipants"
        disabled={participantFilter === participantFilterType.unmuted}
        onClick={handleFilterUnmuted}
      >
        <MicIcon data-testid="micIcon" />
        <span>{unmutedCount}</span>
      </button>
    </div>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    display: 'flex',
    marginRight: -theme.spacing(1), // gives some extra width to center the container (accounts for the right margin of the buttons)
    marginTop: theme.spacing(1), // only 1 because the buttons themselves also have a top margin
    '& button': {
      alignItems: 'center',
      backgroundColor: colors.white,
      borderRadius: '1.2em',
      border: `1px solid ${colors.sentinel}`,
      color: colors.squidInk,
      cursor: 'pointer',
      display: 'flex',
      flex: '0 0 auto',
      fontSize: '1.2em',
      fontWeight: theme.typography.fontWeightBold,
      justifyContent: 'center',
      margin: `${theme.spacing(1)}px ${theme.spacing(1.5)}px 0 0`,
      maxWidth: `calc(100% - ${theme.spacing(1.5)}px)`, // wraps long labels (and accounts for right margin)
      minHeight: '2.4em',
      padding: '2px 12px',
      '& svg': {
        flex: '0 0 auto',
        height: '1.8em',
        margin: '-2px 6px -2px -4px',
        width: 'auto',
        '&.raisedHands': {
          color: colors.agileGreen
        }
      },
      '& span': {
        textAlign: 'left',
        whiteSpace: 'pre'
      },
      '&:disabled': {
        // in this case, the button is disabled when it is selected
        backgroundColor: colors.squidInk,
        borderColor: colors.squidInk,
        color: colors.white,
        cursor: 'default'
      }
    }
  }
}));

export default FilterParticipantsSection;
